import { useEffect, useRef } from "react";

/**
 * Fires `fire()` once per distinct non-null `key`, immediately on the render that first provides
 * it (a navigation event, not a delayed dwell-view — for that see `useDelayedViewTrack`).
 * Re-renders with the same key never refire; a key change (e.g. switching customers) re-arms.
 * Pass `null` while the subject is still resolving; the default key fires once on mount.
 *
 * `fire` is read through a ref, so its identity is deliberately not a trigger — a tracking
 * callback that changes identity later (e.g. the analytics provider re-memoizing after an auth
 * refresh) cannot duplicate an event.
 */
export function useViewTrackedOnce(fire: () => void, key: string | null = ""): void {
  const fireRef = useRef(fire);
  fireRef.current = fire;

  const trackedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (key === null || trackedKeyRef.current === key) return;
    trackedKeyRef.current = key;
    fireRef.current();
  }, [key]);
}
